/**
 * Vista de lista genérica.
 *
 * Dibuja CUALQUIER modelo con `views.list` de su metadata: columnas, orden,
 * decoraciones, filtros y agrupación. No hay ni una línea específica de un
 * módulo acá, y ese es todo el punto — agregar "Liquidación de expensas" al
 * backend no agrega código a este archivo.
 *
 * Tres decisiones que conviene conocer antes de tocarlo:
 *
 * 1. **Los permisos no se adivinan.** `spec.permissions` viene ya evaluado para
 *    el usuario; si `create` es falso no existe el botón, no se dibuja gris.
 * 2. **Archivar no es borrar.** La acción masiva por defecto es archivar
 *    (`active: false`), porque en un ERP contable borrar rompe trazabilidad.
 *    Eliminar existe, es la acción destructiva y pide confirmación aparte.
 * 3. **La tabla scrollea dentro de su caja.** La página nunca scrollea en
 *    horizontal: en un celular eso hace que se pierda la barra de navegación.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FieldDisplay } from '@/components/fields';
import { IconChevronDown, IconChevronRight } from '@/components/shell/icons';
import { recordPath } from '@/components/shell/menu-utils';
import { Button, EmptyState, ErrorState } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { api } from '@/lib/api';
import type { AggregateSpec, Domain, JsonValue, ModelSpec, ReadGroupRow, RecordData } from '@/lib/types';
import { ActionBar } from '@/views/ActionBar';
import { ConfirmDialog } from '@/views/ConfirmDialog';
import { evaluateDomain } from '@/views/domain-eval';
import { esDefinitivo, motivoDe } from '@/views/errores';
import { ExportDialog } from '@/views/ExportDialog';
import { groupDomain, groupKey, splitGroupBy, useGroupLabels } from '@/views/grouping';
import { ImportDialog } from '@/views/ImportDialog';
import { BotonBloqueado } from '@/views/MotivoBloqueo';
import { precargarFicha } from '@/views/precargarFicha';
import { useCompanyId, useInvalidateModel, useRecordsQuery, useReadGroupQuery } from '@/views/queries';
import { SearchPanel, useSearchState } from '@/views/SearchPanel';
import {
  andDomains,
  columnLabel,
  DECORATION_CLASSES,
  existingFields,
  fieldsToFetch,
  isNumeric,
  parseOrder,
  serializeOrder,
  toggleOrder,
  type OrderState,
} from '@/views/view-utils';

const TAMANOS_PAGINA = [20, 50, 80, 200];
const TAMANO_INICIAL = 50;

export interface ListViewProps {
  spec: ModelSpec;
  /**
   * Dominio del menú/acción que trajo al usuario acá. Se aplica SIEMPRE y en AND
   * con los filtros del usuario, que no lo pueden quitar: es lo que hace que
   * «Clientes» sea «Clientes» y no «Contactos».
   */
  baseDomain?: Domain;
  /** Menú de origen, para que los enlaces a un registro no pierdan el contexto. */
  menuName?: string | null;
  /** Alternador de modo de vista, si la acción declara más de uno. */
  modeSwitcher?: ReactNode;
}

/**
 * Columnas que se totalizan, tal como las declaro el backend.
 *
 * Se filtran contra las columnas VISIBLES: totalizar una columna que no se
 * dibuja dejaria un numero al pie de una columna que no existe. Y contra la
 * metadata, con el mismo criterio de siempre — lo que el usuario no puede ver no
 * se le muestra sumado.
 */
function totalesDe(spec: ModelSpec, columnas: string[]): string[] {
  const declarados = spec.views.list?.totals ?? [];
  return declarados.filter((nombre) => columnas.includes(nombre) && spec.fields[nombre] !== undefined);
}

/** `"amount"` -> `"amount:sum"`, que es lo que entiende `read_group`. */
function agregadosDe(totales: string[]): AggregateSpec[] {
  return totales.map((nombre) => `${nombre}:sum` as AggregateSpec);
}

/** Columnas efectivas: las declaradas, o los primeros campos almacenados. */
function columnasDe(spec: ModelSpec): string[] {
  const declaradas = existingFields(spec, spec.views.list?.columns ?? []);
  if (declaradas.length > 0) return declaradas;
  return Object.entries(spec.fields)
    .filter(([, meta]) => meta.store && meta.type !== 'one2many' && meta.type !== 'many2many')
    .sort((a, b) => a[1].priority - b[1].priority)
    .slice(0, 5)
    .map(([nombre]) => nombre);
}

/**
 * Clase de la fila según las decoraciones declaradas. Gana la primera que aplica.
 *
 * Se le pasa la metadata al evaluador: sin ella no puede saber que el `"0.00"`
 * de un importe es un número y no un texto, y toda decoración sobre un decimal
 * da el resultado equivocado.
 */
function claseDecoracion(spec: ModelSpec, fila: RecordData): string | undefined {
  const decoraciones = spec.views.list?.decorations ?? {};
  for (const [nombre, dominio] of Object.entries(decoraciones)) {
    if (evaluateDomain(dominio, fila, spec.fields)) return DECORATION_CLASSES[nombre];
  }
  return undefined;
}

export function ListView({ spec, baseDomain, menuName = null, modeSwitcher }: ListViewProps) {
  const navegar = useNavigate();
  const invalidar = useInvalidateModel();
  const busqueda = useSearchState(spec);

  const [orden, setOrden] = useState<OrderState | null>(() => parseOrder(spec.views.list?.default_order));
  const [limite, setLimite] = useState(TAMANO_INICIAL);
  const [offset, setOffset] = useState(0);
  const [seleccion, setSeleccion] = useState<Set<number>>(() => new Set());
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  /**
   * Por qué el borrado masivo no salió, dicho DENTRO del diálogo que lo pidió.
   *
   * Mismo defecto que en la ficha: el servidor contesta 409 con el motivo y la
   * salida escritos, y la pantalla lo tiraba en un aviso flotante detrás del
   * modal mientras «Eliminar definitivamente» seguía armado. Ver `views/errores`.
   */
  const [errorDeBorrado, setErrorDeBorrado] = useState<{ motivo: string; definitivo: boolean } | null>(
    null,
  );
  const [verArchivados, setVerArchivados] = useState(false);
  const [importando, setImportando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const columnas = useMemo(() => columnasDe(spec), [spec]);
  const campos = useMemo(() => fieldsToFetch(spec, columnas), [spec, columnas]);
  const totales = useMemo(() => totalesDe(spec, columnas), [spec, columnas]);
  /** Dónde va el rótulo «Total de N registros»: la primera columna sin importe. */
  const primeraSinTotal = columnas.findIndex((nombre) => !totales.includes(nombre));

  // Mencionar `active` en el dominio es lo que hace que el backend NO aplique su
  // filtro por defecto (`_apply_active_filter`): así los archivados vuelven a la
  // lista y la acción "Restaurar" tiene sobre qué trabajar.
  const dominio = useMemo(
    () => andDomains(baseDomain, busqueda.domain, verArchivados ? ([['active', 'in', [true, false]]] as Domain) : undefined),
    [baseDomain, busqueda.domain, verArchivados],
  );

  // Cambiar los criterios devuelve a la primera página: quedarse en la 7 de un
  // resultado que ahora tiene 2 muestra una tabla vacía que parece un error.
  useEffect(() => {
    setOffset(0);
    // Se devuelve el mismo Set si ya estaba vacío: crear uno nuevo sería un
    // cambio de estado en cada render y el efecto se realimentaría solo.
    setSeleccion((actual) => (actual.size === 0 ? actual : new Set()));
  }, [dominio, limite, busqueda.groupBy]);

  const parametros = {
    domain: dominio,
    limit: limite,
    offset,
    fields: campos,
    ...(serializeOrder(orden) === undefined ? {} : { order: serializeOrder(orden) as string }),
  };

  const agrupado = busqueda.groupBy !== null;
  const consulta = useRecordsQuery(spec.model, parametros, !agrupado);
  // Los agregados van SIEMPRE que el modelo declare totales: un grupo que dice
  // «Delta Comercial S.A. 20» contesta cuántas ventas, no cuánta plata, que es
  // la pregunta. Con esto el encabezado del grupo dice las dos cosas.
  const grupos = useReadGroupQuery(
    spec.model,
    {
      domain: dominio,
      groupby: busqueda.groupBy === null ? [] : [busqueda.groupBy],
      ...(totales.length > 0 ? { aggregates: agregadosDe(totales) } : {}),
    },
    agrupado,
  );

  const filas = consulta.data?.results ?? [];
  const total = consulta.data?.count ?? 0;
  // El total lo calcula el servidor sobre todo el filtro. Nunca se suma acá: el
  // cliente sólo tiene la página, y un total de la página es un total que miente.
  const sumas = consulta.data?.totals;

  const tieneActive = spec.fields.active !== undefined;
  const puedeArchivar = spec.permissions.write && tieneActive;
  const puedeBorrar = spec.permissions.delete;

  /**
   * Por qué NO se puede borrar lo que está seleccionado, o `null` si se puede.
   *
   * La lista ignoraba `delete_blocked_domain` —que la ficha sí respeta— y por
   * eso **prometía lo que la ficha niega**: se podía seleccionar un comprobante
   * numerado, apretar «Eliminar», leer el diálogo rojo de «la eliminación es
   * definitiva», confirmar… y recibir un error. En la ficha del mismo registro
   * el botón directamente no existe.
   *
   * Se evalúa contra las filas que están en pantalla con el mismo evaluador que
   * usan las decoraciones y el `readonly_domain` del formulario. El servidor
   * sigue siendo la garantía (contesta 403); esto es para no invitar.
   */
  const motivoSinBorrar: string | null = (() => {
    const dominio = spec.delete_blocked_domain;
    const texto = spec.delete_blocked_text;
    if (!Array.isArray(dominio) || dominio.length === 0 || seleccion.size === 0) return null;
    const frenadas = filas.filter(
      (fila) => seleccion.has(fila.id) && evaluateDomain(dominio as Domain, fila, spec.fields),
    );
    if (frenadas.length === 0) return null;
    const cuantas =
      frenadas.length === 1
        ? `1 de los seleccionados no se puede eliminar`
        : `${frenadas.length} de los seleccionados no se pueden eliminar`;
    return `${cuantas}. ${typeof texto === 'string' && texto !== '' ? texto : 'Ese registro está protegido contra el borrado.'} Sacalos de la selección y el resto sí se puede.`;
  })();

  const masivo = useMutation({
    mutationFn: async (accion: { tipo: 'archivar' | 'desarchivar' | 'borrar'; ids: number[] }) => {
      for (const id of accion.ids) {
        if (accion.tipo === 'borrar') await api.deleteRecord(spec.model, id);
        else await api.archiveRecord(spec.model, id, accion.tipo === 'archivar');
      }
      return accion;
    },
    onSuccess: (accion) => {
      setSeleccion(new Set());
      setConfirmarBorrado(false);
      setErrorDeBorrado(null);
      void invalidar(spec.model);
      const cuantos = accion.ids.length;
      const sustantivo = cuantos === 1 ? spec.label.toLowerCase() : spec.label_plural.toLowerCase();
      notify.success(
        accion.tipo === 'borrar'
          ? `Se eliminaron ${cuantos} ${sustantivo}`
          : accion.tipo === 'archivar'
            ? `Se archivaron ${cuantos} ${sustantivo}`
            : `Se restauraron ${cuantos} ${sustantivo}`,
      );
    },
    onError: (error, accion) => {
      // Puede haber quedado a medias: se refresca para mostrar el estado real.
      void invalidar(spec.model);
      if (accion.tipo === 'borrar' && confirmarBorrado) {
        // El diálogo QUEDA ABIERTO y se hace cargo del motivo, que es lo que la
        // persona está mirando. Un aviso flotante detrás del modal no lo lee
        // nadie, y el botón rojo seguía armado invitando a repetirlo.
        setErrorDeBorrado({ motivo: motivoDe(error, 'No se pudo eliminar.'), definitivo: esDefinitivo(error) });
        return;
      }
      notify.error('No se pudo completar la acción', motivoDe(error));
    },
  });

  const todasSeleccionadas = filas.length > 0 && filas.every((fila) => seleccion.has(fila.id));
  const algunaSeleccionada = filas.some((fila) => seleccion.has(fila.id));

  const alternarTodas = (): void => {
    setSeleccion((actual) => {
      const copia = new Set(actual);
      if (todasSeleccionadas) for (const fila of filas) copia.delete(fila.id);
      else for (const fila of filas) copia.add(fila.id);
      return copia;
    });
  };

  const encabezado = (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{spec.label_plural}</h1>
        <p className="mt-1 text-sm text-fg-muted" aria-live="polite">
          {consulta.isPending && !agrupado
            ? 'Cargando…'
            : agrupado
              ? `Agrupado por ${columnLabel(spec, splitGroupBy(busqueda.groupBy ?? '').campo)}`
              : total === 1
                ? '1 registro'
                : `${total} registros`}
        </p>
      </div>
      {/* Sin `shrink-0`: ver la nota de FormView. Un bloque que no puede encoger
          tampoco pliega, y lo que sobra se va de la pantalla. */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
        {modeSwitcher}
        {/* Exportar cuelga del permiso de LECTURA —exportar es leer— y por eso
            no tiene condición: si esta pantalla se está dibujando, el usuario ya
            tiene ese permiso. El backend igual lo vuelve a exigir y arma el
            queryset con las mismas funciones que este listado, así que el
            archivo no puede traer una fila que la tabla de abajo no muestre. */}
        <Button
          onClick={() => {
            setExportando(true);
          }}
        >
          Exportar
        </Button>
        {/* Importar cuelga del MISMO permiso que «Nuevo», porque es lo mismo:
            crear registros. No hay un permiso «puede importar» aparte — sería
            una tercera capa de seguridad que se desincroniza con las otras dos. */}
        {spec.permissions.create && (
          <Button
            onClick={() => {
              setImportando(true);
            }}
          >
            Importar
          </Button>
        )}
        {spec.permissions.create && (
          <Button
            variant="primary"
            onClick={() => {
              navegar(recordPath(spec.model, 'new', menuName));
            }}
          >
            Nuevo
          </Button>
        )}
      </div>
    </div>
  );

  // Se dibuja en las dos salidas del componente: el botón «Importar» vive en el
  // encabezado, que también se muestra cuando el listado falló en cargar, y un
  // botón que abre un diálogo que no está montado no hace nada.
  const dialogoImportar = (
    <>
      <ImportDialog
        spec={spec}
        open={importando}
        onClose={() => {
          setImportando(false);
        }}
        onImported={() => {
          void invalidar(spec.model);
        }}
      />
      {/* El diálogo recibe el dominio y el orden que la pantalla tiene puestos:
          lo que se ve es lo que se exporta. No se recalcula nada acá. */}
      <ExportDialog
        spec={spec}
        open={exportando}
        onClose={() => {
          setExportando(false);
        }}
        domain={dominio}
        order={serializeOrder(orden)}
        includeInactive={verArchivados}
      />
    </>
  );

  if (consulta.isError && !agrupado) {
    return (
      <>
        {encabezado}
        {dialogoImportar}
        <ErrorState
          title={`No se pudieron cargar ${spec.label_plural.toLowerCase()}`}
          message={consulta.error.message}
          onRetry={() => {
            void consulta.refetch();
          }}
        />
      </>
    );
  }

  return (
    <>
      {encabezado}
      <SearchPanel
        spec={spec}
        estado={busqueda}
        extra={
          tieneActive ? (
            <label className="flex items-center gap-2 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={verArchivados}
                onChange={(evento) => {
                  setVerArchivados(evento.target.checked);
                }}
                className="h-4 w-4 rounded border-line accent-[rgb(var(--accent))]"
              />
              Ver archivados
            </label>
          ) : undefined
        }
      />

      {seleccion.size > 0 && (
        <div
          role="region"
          aria-label="Acciones sobre lo seleccionado"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-accent/25 bg-accent-soft px-3 py-2"
        >
          <span className="text-sm font-medium text-accent">
            {seleccion.size === 1 ? '1 seleccionado' : `${seleccion.size} seleccionados`}
          </span>
          <span className="flex-1" />
          {/* Acciones declaradas por el módulo, antes que archivar y eliminar:
              son las del negocio; las otras dos son las del sistema. */}
          <ActionBar
            spec={spec}
            ids={[...seleccion]}
            scope="selection"
            size="sm"
            onDone={() => {
              setSeleccion(new Set());
            }}
          />
          {puedeArchivar && (
            <Button
              size="sm"
              loading={masivo.isPending}
              onClick={() => {
                masivo.mutate({ tipo: 'archivar', ids: [...seleccion] });
              }}
            >
              Archivar
            </Button>
          )}
          {puedeArchivar && (
            <Button
              size="sm"
              variant="ghost"
              loading={masivo.isPending}
              onClick={() => {
                masivo.mutate({ tipo: 'desarchivar', ids: [...seleccion] });
              }}
            >
              Restaurar
            </Button>
          )}
          {puedeBorrar &&
            (motivoSinBorrar === null ? (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  // El motivo del intento anterior no sobrevive a uno nuevo.
                  setErrorDeBorrado(null);
                  setConfirmarBorrado(true);
                }}
              >
                Eliminar
              </Button>
            ) : (
              <BotonBloqueado size="sm" variant="danger" label="Eliminar" motivo={motivoSinBorrar} />
            ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSeleccion(new Set());
            }}
          >
            Quitar selección
          </Button>
        </div>
      )}

      {agrupado ? (
        <ListaAgrupada
          spec={spec}
          columnas={columnas}
          campos={campos}
          menuName={menuName}
          dominioBase={dominio}
          groupBy={busqueda.groupBy as string}
          orden={orden}
          grupos={grupos.data?.groups ?? []}
          totales={totales}
          sumaGeneral={grupos.data?.totals}
          cargando={grupos.isPending}
          error={grupos.isError ? grupos.error.message : null}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse text-sm">
                <caption className="sr-only">
                  {spec.label_plural}
                  {orden !== null && `, ordenado por ${columnLabel(spec, orden.field)}`}
                </caption>
                <thead>
                  <tr className="border-b border-line bg-app-subtle text-left">
                    <th scope="col" className="w-10 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={todasSeleccionadas}
                        ref={(nodo) => {
                          if (nodo !== null) nodo.indeterminate = algunaSeleccionada && !todasSeleccionadas;
                        }}
                        onChange={alternarTodas}
                        aria-label="Seleccionar todo lo que se ve"
                        className="h-4 w-4 rounded border-line accent-[rgb(var(--accent))]"
                      />
                    </th>
                    {columnas.map((nombre) => {
                      const meta = spec.fields[nombre];
                      const activa = orden !== null && orden.field === nombre;
                      return (
                        <th
                          key={nombre}
                          scope="col"
                          aria-sort={activa ? (orden.desc ? 'descending' : 'ascending') : 'none'}
                          className={clsx('px-3 py-2.5 font-medium text-fg-muted', isNumeric(meta) && 'text-right')}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setOrden((actual) => toggleOrder(actual, nombre));
                            }}
                            className={clsx(
                              'inline-flex items-center gap-1 rounded transition-colors hover:text-fg',
                              activa && 'text-fg',
                            )}
                          >
                            {columnLabel(spec, nombre)}
                            <span aria-hidden="true" className={clsx('text-2xs', !activa && 'opacity-0')}>
                              {orden?.desc === true ? '▼' : '▲'}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {consulta.isPending &&
                    Array.from({ length: 6 }, (_, indice) => (
                      <tr key={`esqueleto-${indice}`} className="border-b border-line last:border-0">
                        <td className="px-3 py-3" />
                        {columnas.map((nombre) => (
                          <td key={nombre} className="px-3 py-3">
                            <span className="block h-3 w-full max-w-[10rem] animate-pulse rounded bg-surface-raised" />
                          </td>
                        ))}
                      </tr>
                    ))}

                  {!consulta.isPending &&
                    filas.map((fila) => (
                      <FilaRegistro
                        key={fila.id}
                        spec={spec}
                        fila={fila}
                        columnas={columnas}
                        menuName={menuName}
                        seleccionada={seleccion.has(fila.id)}
                        onSeleccionar={(marcada) => {
                          setSeleccion((actual) => {
                            const copia = new Set(actual);
                            if (marcada) copia.add(fila.id);
                            else copia.delete(fila.id);
                            return copia;
                          });
                        }}
                      />
                    ))}
                </tbody>

                {/* El pie con los totales del FILTRO, no de la página. Se dibuja
                    sólo si el modelo declaró qué se totaliza y hay algo que
                    mostrar: una tabla sin plata no gana una fila vacía. */}
                {!consulta.isPending && filas.length > 0 && totales.length > 0 && sumas !== undefined && (
                  <tfoot>
                    <tr className="border-t-2 border-line bg-app-subtle font-medium">
                      <td className="px-3 py-2.5" />
                      {columnas.map((nombre, indice) => {
                        const meta = spec.fields[nombre];
                        if (meta !== undefined && totales.includes(nombre)) {
                          return (
                            <td key={nombre} className="px-3 py-2.5 text-right tabular-nums">
                              <FieldDisplay meta={meta} value={sumas[nombre] ?? null} compact />
                            </td>
                          );
                        }
                        // El rótulo va en la primera columna que NO se totaliza:
                        // pisar un importe con la palabra «Total» sería tapar
                        // justamente el número que se vino a leer.
                        if (indice === primeraSinTotal) {
                          return (
                            <td key={nombre} className="px-3 py-2.5 text-fg-muted">
                              {total === 1 ? 'Total de 1 registro' : `Total de ${total} registros`}
                            </td>
                          );
                        }
                        return <td key={nombre} className="px-3 py-2.5" />;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {!consulta.isPending && filas.length === 0 && (
              <div className="p-4">
                <EmptyState
                  title={
                    busqueda.hayCriterios
                      ? 'Ningún resultado con esos criterios'
                      : `Todavía no hay ${spec.label_plural.toLowerCase()}`
                  }
                  description={
                    busqueda.hayCriterios
                      ? 'Probá con menos filtros o revisá cómo escribiste la búsqueda.'
                      : // Sin artículo delante de la etiqueta: el género lo pone
                        // el modelo («Cuando cargues comprobante…» no se puede
                        // leer, y «el/la» exigiría que la metadata declare género
                        // en 99 modelos). Así la frase es correcta para
                        // «Comprobantes», «Facturas» y «Unidades funcionales».
                        'Lo que cargues va a aparecer acá.'
                  }
                  action={
                    busqueda.hayCriterios ? (
                      <Button onClick={busqueda.limpiar}>Limpiar filtros</Button>
                    ) : spec.permissions.create ? (
                      <Button
                        variant="primary"
                        onClick={() => {
                          navegar(recordPath(spec.model, 'new', menuName));
                        }}
                      >
                        Crear {spec.label.toLowerCase()}
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            )}
          </div>

          {total > 0 && (
            <nav
              aria-label="Paginación"
              className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted"
            >
              <span aria-live="polite">
                {offset + 1}–{Math.min(offset + limite, total)} de {total}
              </span>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5">
                  <span className="sr-only sm:not-sr-only">Por página</span>
                  <select
                    value={limite}
                    onChange={(evento) => {
                      setLimite(Number(evento.target.value));
                    }}
                    className="h-9 rounded-md border border-line bg-surface px-2 text-sm"
                    aria-label="Registros por página"
                  >
                    {TAMANOS_PAGINA.map((tamano) => (
                      <option key={tamano} value={tamano}>
                        {tamano}
                      </option>
                    ))}
                  </select>
                </label>

                <Button
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => {
                    setOffset((actual) => Math.max(0, actual - limite));
                  }}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  disabled={offset + limite >= total}
                  onClick={() => {
                    setOffset((actual) => actual + limite);
                  }}
                >
                  Siguiente
                </Button>
              </div>
            </nav>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmarBorrado}
        tone="danger"
        title={`¿Eliminar ${seleccion.size === 1 ? spec.label.toLowerCase() : `${seleccion.size} ${spec.label_plural.toLowerCase()}`}?`}
        description="La eliminación es definitiva y no queda registro. Si solo querés sacarlos de la vista, archivalos: se pueden restaurar."
        confirmLabel="Eliminar definitivamente"
        loading={masivo.isPending}
        error={errorDeBorrado?.motivo ?? null}
        canRetry={!(errorDeBorrado?.definitivo ?? false)}
        exit={
          // La salida se ofrece SÓLO cuando el borrado ya falló y falló para
          // siempre: antes de eso, archivar es una decisión de la persona.
          errorDeBorrado?.definitivo === true && puedeArchivar
            ? {
                label: 'Archivar',
                hint: 'Dejan de aparecer en las listas y la contabilidad queda intacta. Se pueden restaurar.',
                onClick: () => {
                  masivo.mutate({ tipo: 'archivar', ids: [...seleccion] });
                },
                loading: masivo.isPending,
              }
            : null
        }
        onConfirm={() => {
          masivo.mutate({ tipo: 'borrar', ids: [...seleccion] });
        }}
        onCancel={() => {
          setConfirmarBorrado(false);
          setErrorDeBorrado(null);
        }}
      />

      {dialogoImportar}
    </>
  );
}

// ---------------------------------------------------------------------------
// Fila
// ---------------------------------------------------------------------------
/**
 * Pedir de antemano datos y botonera de la ficha cuando la persona deja el
 * mouse sobre una fila (o llega a ella con el teclado). Medido con la demora
 * real: entre apuntar y hacer clic pasan unos cientos de milisegundos, y es
 * justo lo que cuesta un pedido. La espera corta evita pedir fichas de cada
 * fila que el mouse cruza al pasar; lo ya pedido y fresco no se repite.
 */
const ESPERA_ANTES_DE_PRECARGAR_MS = 150;

function usePrecargaDeFila(modelo: string, id: number) {
  const cliente = useQueryClient();
  const companyId = useCompanyId();
  const temporizador = useRef<number | null>(null);
  const cancelar = (): void => {
    if (temporizador.current !== null) window.clearTimeout(temporizador.current);
    temporizador.current = null;
  };
  useEffect(() => cancelar, []);
  return {
    onPointerEnter: () => {
      cancelar();
      temporizador.current = window.setTimeout(() => {
        precargarFicha(cliente, companyId, modelo, id, { completo: false });
      }, ESPERA_ANTES_DE_PRECARGAR_MS);
    },
    onPointerLeave: cancelar,
    onFocus: () => {
      precargarFicha(cliente, companyId, modelo, id, { completo: false });
    },
  };
}

function FilaRegistro({
  spec,
  fila,
  columnas,
  menuName,
  seleccionada,
  onSeleccionar,
}: {
  spec: ModelSpec;
  fila: RecordData;
  columnas: string[];
  menuName: string | null;
  seleccionada: boolean;
  onSeleccionar: (marcada: boolean) => void;
}) {
  const decoracion = claseDecoracion(spec, fila);
  const archivado = fila.active === false;
  const precarga = usePrecargaDeFila(spec.model, fila.id);

  return (
    <tr
      onPointerEnter={precarga.onPointerEnter}
      onPointerLeave={precarga.onPointerLeave}
      onFocus={precarga.onFocus}
      className={clsx(
        'border-b border-line transition-colors last:border-0 hover:bg-surface-raised',
        seleccionada && 'bg-accent-soft/50',
        archivado && 'opacity-60',
        decoracion,
      )}
    >
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          checked={seleccionada}
          onChange={(evento) => {
            onSeleccionar(evento.target.checked);
          }}
          aria-label={`Seleccionar ${fila.display_name}`}
          className="h-4 w-4 rounded border-line accent-[rgb(var(--accent))]"
        />
      </td>

      {columnas.map((nombre, indice) => {
        const meta = spec.fields[nombre];
        if (meta === undefined) return <td key={nombre} className="px-3 py-2.5" />;
        return (
          <td key={nombre} className={clsx('px-3 py-2.5', isNumeric(meta) && 'text-right')}>
            {/* La primera columna es el enlace al registro: una fila entera
                clickeable no es alcanzable por teclado ni anunciable. */}
            {indice === 0 ? (
              <Link
                to={recordPath(spec.model, fila.id, menuName)}
                className="font-medium text-fg hover:text-accent hover:underline"
              >
                <FieldDisplay meta={meta} value={fila[nombre] ?? null} compact />
              </Link>
            ) : (
              <FieldDisplay meta={meta} value={fila[nombre] ?? null} compact />
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Modo agrupado
// ---------------------------------------------------------------------------
function ListaAgrupada({
  spec,
  columnas,
  campos,
  menuName,
  dominioBase,
  groupBy,
  orden,
  grupos,
  totales,
  sumaGeneral,
  cargando,
  error,
}: {
  spec: ModelSpec;
  columnas: string[];
  campos: string[];
  menuName: string | null;
  dominioBase: Domain;
  groupBy: string;
  orden: OrderState | null;
  grupos: ReadGroupRow[];
  /** Columnas que se suman; el valor de cada grupo llega como `campo:sum`. */
  totales: string[];
  /** Total del filtro entero, ya sumado por el servidor. */
  sumaGeneral: Record<string, JsonValue> | undefined;
  cargando: boolean;
  error: string | null;
}) {
  const valores: JsonValue[] = grupos.map((grupo) => grupo[groupBy] ?? null);
  const etiquetar = useGroupLabels(spec, groupBy, valores);
  const [abiertos, setAbiertos] = useState<Set<string>>(() => new Set());

  if (error !== null) {
    return <ErrorState title="No se pudo agrupar" message={error} />;
  }

  if (cargando) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 4 }, (_, indice) => (
          <div key={indice} className="h-12 animate-pulse rounded-lg border border-line bg-surface" />
        ))}
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <EmptyState
        title="Ningún resultado"
        description="No hay registros que agrupar con los criterios actuales."
      />
    );
  }

  return (
    <div className="space-y-2">
      {grupos.map((grupo) => {
        const valor = grupo[groupBy] ?? null;
        const clave = groupKey(valor);
        const abierto = abiertos.has(clave);
        const idPanel = `grupo-${clave.replace(/[^a-zA-Z0-9_-]/g, '')}`;

        return (
          <div key={clave} className="overflow-hidden rounded-lg border border-line bg-surface">
            <h2>
              <button
                type="button"
                aria-expanded={abierto}
                aria-controls={idPanel}
                onClick={() => {
                  setAbiertos((actual) => {
                    const copia = new Set(actual);
                    if (copia.has(clave)) copia.delete(clave);
                    else copia.add(clave);
                    return copia;
                  });
                }}
                className="flex w-full items-center gap-2 px-3 py-3 text-left transition-colors hover:bg-surface-raised"
              >
                {abierto ? (
                  <IconChevronDown className="h-4 w-4 shrink-0 text-fg-muted" />
                ) : (
                  <IconChevronRight className="h-4 w-4 shrink-0 text-fg-muted" />
                )}
                <span className="min-w-0 flex-1 truncate font-medium">{etiquetar(valor)}</span>
                {/* La plata del grupo ANTES del contador y en el color del
                    texto: agrupar por Cliente contestaba «Delta Comercial S.A.
                    20», que dice cuántas ventas y no cuánto se le vendió — que
                    es la pregunta. Ahora dice las dos cosas, y la que se lee
                    primero es la que se vino a buscar. */}
                {totales.map((nombre) => {
                  const meta = spec.fields[nombre];
                  if (meta === undefined) return null;
                  return (
                    <span key={nombre} className="shrink-0 text-sm font-medium tabular-nums">
                      <FieldDisplay meta={meta} value={grupo[`${nombre}:sum`] ?? null} compact />
                    </span>
                  );
                })}
                <span className="shrink-0 rounded-full bg-surface-raised px-2 py-0.5 text-xs tabular-nums text-fg-muted">
                  {grupo.__count}
                </span>
              </button>
            </h2>

            {abierto && (
              <div id={idPanel}>
                <FilasDeGrupo
                  spec={spec}
                  columnas={columnas}
                  campos={campos}
                  menuName={menuName}
                  dominio={andDomains(dominioBase, groupDomain(groupBy, valor, spec.fields[splitGroupBy(groupBy).campo]))}
                  orden={orden}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* El total de TODOS los grupos. Sin esto, agrupar por Cliente y filtrar
          por «A administradores» dejaba tres subtotales y ningún total, que es
          la pregunta que se estaba haciendo. */}
      {totales.length > 0 && sumaGeneral !== undefined && (
        <div className="flex items-center gap-2 rounded-lg border border-line bg-app-subtle px-3 py-3">
          <span className="min-w-0 flex-1 truncate font-medium">
            {grupos.length === 1 ? 'Total del grupo' : `Total de los ${grupos.length} grupos`}
          </span>
          {totales.map((nombre) => {
            const meta = spec.fields[nombre];
            if (meta === undefined) return null;
            return (
              <span key={nombre} className="shrink-0 font-medium tabular-nums">
                <FieldDisplay meta={meta} value={sumaGeneral[nombre] ?? null} compact />
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Filas de un grupo desplegado. Se piden recién al abrirlo, no antes. */
function FilasDeGrupo({
  spec,
  columnas,
  campos,
  menuName,
  dominio,
  orden,
}: {
  spec: ModelSpec;
  columnas: string[];
  campos: string[];
  menuName: string | null;
  dominio: Domain;
  orden: OrderState | null;
}) {
  const consulta = useRecordsQuery(spec.model, {
    domain: dominio,
    limit: 80,
    fields: campos,
    ...(serializeOrder(orden) === undefined ? {} : { order: serializeOrder(orden) as string }),
  });

  if (consulta.isPending) {
    return <p className="px-3 py-4 text-sm text-fg-muted">Cargando…</p>;
  }
  if (consulta.isError) {
    return (
      <p role="alert" className="px-3 py-4 text-sm text-danger">
        {consulta.error.message}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto border-t border-line">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <caption className="sr-only">Registros del grupo</caption>
        <thead className="sr-only">
          <tr>
            {columnas.map((nombre) => (
              <th key={nombre} scope="col">
                {columnLabel(spec, nombre)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {consulta.data.results.map((fila) => (
            <tr key={fila.id} className="border-b border-line last:border-0 hover:bg-surface-raised">
              {columnas.map((nombre, indice) => {
                const meta = spec.fields[nombre];
                if (meta === undefined) return <td key={nombre} className="px-3 py-2" />;
                return (
                  <td key={nombre} className={clsx('px-3 py-2', isNumeric(meta) && 'text-right')}>
                    {indice === 0 ? (
                      <Link
                        to={recordPath(spec.model, fila.id, menuName)}
                        className="font-medium hover:text-accent hover:underline"
                      >
                        <FieldDisplay meta={meta} value={fila[nombre] ?? null} compact />
                      </Link>
                    ) : (
                      <FieldDisplay meta={meta} value={fila[nombre] ?? null} compact />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
